import { skeemaApi } from '../skeema'
import {
  SMART_SESSION_ENDPOINT_PATH,
  SMART_SESSION_PAGES_ENDPOINT_PATH,
  SMART_SESSIONS_ENDPOINT_PATH_V2,
  SmartSessionEndpointType,
  SmartSessionPagesEndpointType,
  SmartSessionsEndpointV2Type,
} from '../../../models/endpoints.types'
import { TablistPageType } from '../../../models/tablist_pages.types'
import { SmartSessionType } from '../../../models/smart_sessions.types'
import { deleteSession, addSession, deleteSessionPage, addSessionPage } from '../../sessionSlice'

export const smartSessionsApi = skeemaApi.injectEndpoints({
  endpoints: (builder) => ({
    getSmartSessionsWithOpenTabsV2: builder.query<
      SmartSessionsEndpointV2Type['POST']['response'],
      {
        openTabs?: TablistPageType[]
        from_ts?: number
        to_ts?: number
      }
    >({
      query: ({ openTabs, from_ts, to_ts }) => {
        const open_tabs =
          openTabs === undefined
            ? []
            : openTabs
                .map((t) => ({
                  tab_id: t.id,
                  url: t.url,
                  title: t.title,
                  favicon_url: t.favicon_url,
                  window_id: t.window_id,
                  last_access_time_ms: t.last_access_timestamp_ms,
                }))
                .filter((t) => {
                  return !!t.url && !!t.title
                })

        return {
          url: `${SMART_SESSIONS_ENDPOINT_PATH_V2}`,
          method: 'POST',
          body: {
            open_tabs,
            from_ts,
            to_ts,
          },
        }
      },
      serializeQueryArgs: ({ queryArgs, endpointDefinition, endpointName }) => {
        const { openTabs, from_ts, to_ts } = queryArgs
        // Avoid refetches triggerred by updated open tab timestamps
        const openTabsWithoutTimestamps = openTabs?.map((t) => ({
          tab_id: t.id,
          url: t.url,
          title: t.title,
          favicon_url: t.favicon_url,
          window_id: t.window_id,
        }))

        return {
          queryArgs: {
            openTabs: openTabsWithoutTimestamps,
            from_ts,
            to_ts,
          },
          endpointDefinition,
          endpointName,
        }
      },
      providesTags: (result) =>
        result
          ? result.sessions.map((session) => ({ type: 'SmartSessions', id: session.id }))
          : ['SmartSessions'],
    }),
    deleteSmartSession: builder.mutation<
      SmartSessionEndpointType['DELETE']['response'],
      SmartSessionType
    >({
      query: (smart_session) => {
        const { id } = smart_session
        return {
          url: SMART_SESSION_ENDPOINT_PATH(id.toString()),
          method: 'DELETE',
        }
      },
      onQueryStarted: async (smart_session, { dispatch, queryFulfilled }) => {
        await queryFulfilled
          .catch(() => {
            dispatch(addSession(smart_session))
          })
          .then(() => {
            dispatch(deleteSession(smart_session))
          })
      },
      invalidatesTags: (result, error, smart_session) => [
        { type: 'SmartSessions', id: smart_session.id },
      ],
    }),
    deleteSmartSessionPage: builder.mutation<
      SmartSessionPagesEndpointType['DELETE']['response'],
      { session: SmartSessionType; page: TablistPageType }
    >({
      query: ({ session, page }) => {
        const { id: session_id } = session
        return {
          url: `${SMART_SESSION_PAGES_ENDPOINT_PATH(session_id.toString())}${page.instance_id}/`,
          method: 'DELETE',
        }
      },
      onQueryStarted: async ({ session, page }, { dispatch, queryFulfilled }) => {
        const { id: session_id } = session
        const currentPageIdx = session.pages.findIndex((p) => p.instance_id === page.instance_id)
        if (currentPageIdx !== -1) {
          dispatch(deleteSessionPage({ session_id, idx: currentPageIdx }))
          await queryFulfilled.catch(() => {
            dispatch(addSessionPage({ session_id, page, idx: currentPageIdx }))
          })
        }
      },
      invalidatesTags: ['SmartSessions'],
    }),
  }),
})

export const {
  useGetSmartSessionsWithOpenTabsV2Query,
  useLazyGetSmartSessionsWithOpenTabsV2Query,
  useDeleteSmartSessionMutation,
  useDeleteSmartSessionPageMutation,
} = smartSessionsApi
